import { differenceInMinutes, isAfter, isPast } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Yup from "yup";

import { fetchMaxWorkersByLocationJob } from "../../../../../../../js/features/ManageJobs/ManageJobsSlice";
import { calculateShiftHoursAndMinutes } from "../../../../../../utils/helpers";
import { getShiftPurposeDetailField } from "../../../../../../utils/shift-purpose-detail-field";
import useSchedulerData from "../../../../controllers/use-scheduler-data";
import {
  configureDatesOfTheWeek,
  getInitValues,
  getYupInitObject,
  sanetizeDateTime,
} from "../utils";

type JobRole = {
  id: number;
  isActive: boolean;
  name: string;
  hexColor: string;
};

type Shift = {
	id: number | string;
};

type FormValues = {
	startTime: string;
	endTime: string;
	numberOfWorkersNeeded: string;
};

const useEditShiftDialog = () => {
	const { t } = useTranslation();
	const [filteredJobs, setFilteredJobs] = useState<JobRole[]>([]);

	const {
		allShiftsData,
		shiftIdForEditShift,
		defaultDurationHrs,
		minDurationHrs,
		maxDurationHrs,
		shiftId,
		filterJobRoles,
		filterAllWorkcenters,
		filterAllLocations,
		shiftPurposes,
		isShiftPurposeEnabled,
	} = useSchedulerData();

	const dispatch = useDispatch();

	const currentShiftDetails = useCallback(() => {
    return allShiftsData.find(
      (shift: Shift) => shift.id.toString() === shiftIdForEditShift?.toString()
    );
	}, [allShiftsData, shiftIdForEditShift]);

	const initialValues = useMemo(
		() =>
			getInitValues({
				allShiftsData,
				shiftIdForEditShift,
				currentShiftDetails,
				filterAllWorkcenters,
			}),
		[
			allShiftsData,
			shiftIdForEditShift,
			currentShiftDetails,
			filterAllWorkcenters,
		]
	);

	const schema = useMemo(() => {
		return Yup.object({
			...getYupInitObject(t),
			shiftPurposeDetail: Yup.string().test(
				"shift-purpose-detail",
				function (value) {
					const { shiftPurposeId } = this.parent;
					const detailField = getShiftPurposeDetailField(
						shiftPurposes,
						shiftPurposeId
					);
					if (!isShiftPurposeEnabled || !detailField) return true;
					const detail = value?.trim() ?? "";
					if (detailField.isRequired && detail.length === 0) {
						return this.createError({
							message: t("scheduler_purpose-detail-required", {
								label: detailField.label,
							}),
						});
					}
					if (detail.length > detailField.maxLength) {
						return this.createError({
							message: t("scheduler_purpose-detail-max-length", {
								count: detailField.maxLength,
							}),
						});
					}
					return true;
				}
			),
			endTime: Yup.string()
				.required(t("scheduler_end-time-required"))
				.test(
					"is-greater",
					t("scheduler_end-time-greater-start"),
					function (value: string | undefined) {
						const { startTime } = this.parent || {};
						return value ? isAfter(new Date(value), new Date(startTime)) : !!value;
					}
				)
				.test(
					"min-signup-duration",
					function (value) {
						const { startTime, locationId } = this.parent;
						if (!startTime || !value || !locationId) return true;

						const selectedLocation = filterAllLocations?.find(
							(l: any) => l.id === Number(locationId)
						);

						const minHours =
							(selectedLocation as any)?.minShiftDuration ?? minDurationHrs;

						const shiftDuration =
							differenceInMinutes(new Date(value), new Date(startTime)) / 60;

						return (
							shiftDuration >= minHours ||
							this.createError({
								message: t("scheduler_signup-duration-min", {
									count: minHours,
									unit:
										minHours > 1
											? t("scheduler_hours-unit")
											: t("scheduler_hour-unit"),
								}),
							})
						);
					}
				)
				.test(
					"max-signup-duration",
					function (value) {
						const { startTime, locationId } = this.parent;
						if (!startTime || !value || !locationId) return true;

						const selectedLocation = filterAllLocations?.find(
							(l: any) => l.id === Number(locationId)
						);

						const maxHours =
							(selectedLocation as any)?.maxShiftDuration ?? maxDurationHrs;

						const shiftDuration =
							differenceInMinutes(new Date(value), new Date(startTime)) / 60;

						return (
							shiftDuration <= maxHours ||
							this.createError({
								message: `Signup duration must be at most ${maxHours} hour${
									maxHours > 1 ? "s" : ""
								}`,
							})
						);
					}
				),
			allowPartialSignups: Yup.boolean().test(
				"min-partial-signup-duration",
				t("scheduler_partial-min-signup-max-total"),
				function (value) {
					if (!value) return true;
					const { startTime, endTime, locationId } = this.parent;
					if (!startTime || !endTime || !locationId) return true;

					const selectedLocation = filterAllLocations?.find(
						(l: any) => l.id === Number(locationId)
					);
					const minHours = selectedLocation?.partialShiftMinHours ?? 0;

					const shiftDuration =
						differenceInMinutes(new Date(endTime), new Date(startTime)) / 60;

					return shiftDuration >= minHours;
				}
			),
			isFlexiSignupEnabled: Yup.boolean().test(
				"min-flexi-shift-duration",
				function (value) {
					if (!value) return true;
					const { startTime, endTime, locationId } = this.parent;
					if (!startTime || !endTime || !locationId) return true;

					const shiftDuration =
						differenceInMinutes(new Date(endTime), new Date(startTime)) / 60;

					const selectedLocation = filterAllLocations?.find(
						(l: any) => l.id === Number(locationId)
					);
					const minHours = selectedLocation?.flexiShiftMinHours ?? 1;

					return (
						shiftDuration >= minHours ||
						this.createError({
							message: t("scheduler_min-flexi-shift-duration", {
								count: minHours,
								unit:
									minHours > 1
										? t("scheduler_hours-unit")
										: t("scheduler_hour-unit"),
							}),
						})
					);
				}
			),
			minFlexiSignupHours: Yup.number().when("isFlexiSignupEnabled", {
				is: true,
				then: Yup.number()
					.required(t("scheduler_min-signup-duration-required"))
					.integer(t("scheduler_must-be-integer"))
					.min(1, ({ min }) =>
						t("scheduler_min-signup-duration-hours", {
							count: min,
							unit:
								min > 1 ? t("scheduler_hours-unit") : t("scheduler_hour-unit"),
						})
					)
					.test(
						"max-shift-duration",
						t("scheduler_min-signup-less-than-total"),
						function (value) {
							const { startTime, endTime } = this.parent;
							if (!startTime || !endTime || !value) return true;
							const shiftDurationHours =
								differenceInMinutes(new Date(endTime), new Date(startTime)) /
								60;
							return value < shiftDurationHours;
						}
					),
			}),
		});
	}, [
		filterAllLocations,
		minDurationHrs,
		maxDurationHrs,
		shiftPurposes,
		isShiftPurposeEnabled,
		t,
	]);

	const [totalHours, setTotalHours] = useState(0);
	const [totalMins, setTotalMins] = useState(0);

	const [initialShiftDateTimeValues, setInitialShiftDateTimeValue] =
		useState(initialValues);
	const [selectedDatesOfTheWeek, setSelectedDatesOfTheWeek] = useState(() =>
		configureDatesOfTheWeek(new Date(initialValues.startTime))
	);

	const handleWorkcenterChange = useCallback(
		(workcenterId: string) => {
			const selectedWorkcenter = filterAllWorkcenters.find(
				(workcenter: { id: number }) => workcenter.id === +workcenterId
			);

			if (!selectedWorkcenter?.jobIds) {
				setFilteredJobs([]);
				return;
			}

			const jobIds = selectedWorkcenter.jobIds.split(",").map(Number);

			const jobs = filterJobRoles.filter(
				(job: JobRole) => jobIds.includes(job.id) && job.isActive
			);

			setFilteredJobs(jobs);
		},
		[filterJobRoles, filterAllWorkcenters]
	);

	const getNumberOfStaff = useCallback(
		(
			jobRole: string | null,
			locationId: string,
			workCenterId: string | number,
			startTime: string,
			endTime: string,
			shiftSkills?: any[],
			allowPartialSignups?: boolean,
			allowedWorkerTypeIds?: number[] | null
		) => {
			if (!locationId || !startTime || !endTime || !workCenterId) return;
			if (jobRole === "" && shiftSkills?.length === 0) return;

			dispatch(
				fetchMaxWorkersByLocationJob({
					locationId,
					jobId: jobRole === "" ? null : jobRole,
					workcenterId: Number(workCenterId),
					startDateTime: sanetizeDateTime(startTime),
					endDateTime: sanetizeDateTime(endTime),
					shiftSkills,
					isPartialSignupEnabled: allowPartialSignups,
					allowedWorkerTypeIds: allowedWorkerTypeIds ?? null,
				})
			);
		},
		[dispatch]
	);

	const calculateHours = useCallback(
		(startTime: string, endTime: string, numberOfWorkersNeeded: string) => {
			const { hours, minutes } = calculateShiftHoursAndMinutes(
				startTime,
				endTime,
				numberOfWorkersNeeded
			);

			setTotalHours(hours);
			setTotalMins(minutes);
		},
		[]
	);

	const calculateTotalHoursFromWorkers = useCallback(
		(formValues: FormValues, numberOfWorkersNeeded: string) => {
			const { startTime, endTime } = formValues;
			if (startTime && endTime && numberOfWorkersNeeded) {
				calculateHours(startTime, endTime, numberOfWorkersNeeded);
			}
		},
		[calculateHours]
	);

	const calculateTotalHoursFromStartTime = (
		formValues: FormValues,
		startTime: string
	) => {
		const { numberOfWorkersNeeded, endTime } = formValues;
		if (startTime && endTime && numberOfWorkersNeeded) {
			calculateHours(startTime, endTime, numberOfWorkersNeeded);
		}
	};

	const calculateTotalHoursFromEndTime = (
		formValues: FormValues,
		endTime: string
	) => {
		const { numberOfWorkersNeeded, startTime } = formValues;
		if (startTime && endTime && numberOfWorkersNeeded) {
			calculateHours(startTime, endTime, numberOfWorkersNeeded);
		}
	};

	const foundJob = (jobId: number) => {
		return filteredJobs.some((job: JobRole) => job.id === jobId);
	};

	useEffect(() => {
		const initValues = getInitValues({
			allShiftsData,
			shiftIdForEditShift,
			currentShiftDetails,
			filterAllWorkcenters,
		});

		handleWorkcenterChange(initValues.workCenterId);
		getNumberOfStaff(
			initValues.jobId ?? null,
			initValues.locationId,
			initValues.workCenterId,
			initValues.startTime,
			initValues.endTime,
			initValues.shiftSkills,
			initValues.allowPartialSignups,
			initValues.allowedWorkerTypeIds
		);
		calculateTotalHoursFromWorkers(
			initValues,
			initValues.numberOfWorkersNeeded
		);
	}, [
		defaultDurationHrs,
		allShiftsData,
		shiftIdForEditShift,
		currentShiftDetails,
		minDurationHrs,
		maxDurationHrs,
		handleWorkcenterChange,
		getNumberOfStaff,
		calculateTotalHoursFromWorkers,
		filterAllWorkcenters,
	]);

	const isDisabled = (currentDate: Date) => {
		return isPast(currentDate) || false;
	};

	return [
		{
			initialValues,
			schema,
			initialShiftDateTimeValues,
			filteredJobs,
			selectedDatesOfTheWeek,
			totalHours,
			totalMins,
			shiftId,
		},
		{
			isDisabled,
			calculateTotalHoursFromEndTime,
			calculateTotalHoursFromStartTime,
			calculateTotalHoursFromWorkers,
			handleWorkcenterChange,
			getNumberOfStaff,
			setInitialShiftDateTimeValue,
			setSelectedDatesOfTheWeek,
			setTotalHours,
			foundJob,
		},
	] as const;
};

export default useEditShiftDialog;
